import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { LoadingDots } from '@app/ui';

import VerbalMentionVideoModal from './VerbalMentionVideoModal';
import BroadcastVerbalMentionPDF from '../../pdfs/BroadcastVerbalMentionPDF';
import connect from '@app/utils/connect';
import filterActions from '@app/actions/filters';
import { useVerbalMentionsTableStore } from '../../hooks/useVerbalMentionsTable';
import { Client } from '@app/types/api';
import { parseEndDate, parseStartDate } from '@app/utils/parseDate';
import { getFiltersToSave } from '../../utils/broadcastsUtils';
import usePendo from '@app/components/hooks/usePendo';
import { Notification } from '@app/components/notifications/Notifications';
import RouteNames from '../../../RouteNames';
import useGeneratePDF from '../../../broadcasts/hooks/useGeneratePDF';
import { DEFAULT_LANGUAGE } from '../../../i18n/constants';

const pdfTestDomNode = document.getElementById('broadcast-details-modal');

interface VerbalMentionVideoWrapperProps {
    filters: any;
    client: Client;
    saveFilters: (newFilters: any) => void;
    sendNotifications: (
        message: string,
        config?: Notification['options'],
    ) => void;
}

const VerbalMentionVideoWrapper = ({
    filters,
    client,
    saveFilters,
    sendNotifications,
}: VerbalMentionVideoWrapperProps) => {
    const [isloadingFilters, setIsloadingFilters] = useState(false);
    const { pendo } = usePendo();
    const { i18n } = useTranslation();
    const {
        broadcastName,
        contentDate,
        networkId,
        rightsholderId,
        sponsorId,
        mentionId,
        pdf,
    } = useParams();
    const { verbalMentionsData, isLoadingVerbalMentions } =
        useVerbalMentionsTableStore();
    const navigate = useNavigate();
    const { isDownloadingReport, generatePDFUtils } = useGeneratePDF();

    const mention = verbalMentionsData.get(
        `${mentionId}-${networkId}-${broadcastName}`,
    );

    const trackObject = useMemo(() => {
        if (!mention) return null;

        return {
            vm_detection_id: mention.mentionId,
            broadcast_name: mention.broadcastName,
            rightsholder: mention.rightsholder.name,
            sponsor: mention.sponsor.name,
            mvp: mention.averageMvpScore,
            SMV: mention.sponsorMediaValue,
            network: mention.network.name,
        };
    }, [mentionId, networkId, broadcastName]);

    useEffect(() => {
        if (trackObject && pendo) {
            pendo.track(
                'Relo Edge::Broadcast Exposure::Verbal Mentions::Exposure Detail::View',
                trackObject,
            );
        }
    }, [trackObject, pendo]);

    useEffect(() => {
        if (!mention) {
            const start = parseStartDate(contentDate);
            const end = parseEndDate(contentDate);
            const newFilters = getFiltersToSave({
                ...filters,
                startDate: start,
                endDate: end,
                rightsholders: {
                    ...filters.rightsholders,
                    selected: [rightsholderId],
                },
                sponsors: {
                    ...filters.sponsors,
                    selected: [sponsorId],
                },
                broadcastNetworks: {
                    ...filters.broadcastNetworks,
                    selected: [networkId],
                },
            });
            setIsloadingFilters(true);
            saveFilters(newFilters);
            setIsloadingFilters(false);
        }
    }, [mention, filters.isLoading]);

    const getReport = async () => {
        pendo.track(
            'Relo Edge::Broadcast Exposure::Verbal Mentions::Exposure Detail::Download',
            trackObject,
        );

        const dateStr = new Date(contentDate)
            .toLocaleDateString('en-US')
            .replace(/\//g, '-');

        const filename =
            `Broadcast_${mention.rightsholder.name}_${dateStr}`.replace(
                /\s/g,
                '',
            );

        generatePDFUtils({
            component: (
                <BroadcastVerbalMentionPDF
                    broadcastName={broadcastName}
                    verbalMention={mention}
                    currencyCode={client.currency.code}
                    videoUrl={window.location.href}
                />
            ),
            filename,
            sendNotifications,
            language: i18n.language || DEFAULT_LANGUAGE,
        });
    };

    const handleClose = () => {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const modalSegmentsLength =
            RouteNames.verbalMentionModal.path.split('/').length;
        const backPath =
            '/' +
            pathSegments
                .slice(
                    0,
                    -(pdf ? modalSegmentsLength : modalSegmentsLength - 1),
                )
                .join('/');

        navigate(backPath, { replace: true });
    };

    if (isloadingFilters || isLoadingVerbalMentions) {
        return <LoadingDots size="lg" className="-p-t-6 -m-t-6" />;
    }

    if (!mention) {
        return null;
    }

    if (pdf) {
        return ReactDOM.createPortal(
            <div
                className="modal-overlay modal-shown"
                style={{ zIndex: 1200, overflow: 'scroll' }}
                onClick={handleClose}
            >
                <div
                    className="modal-v2-wrapper"
                    style={{
                        maxWidth: '90%',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <BroadcastVerbalMentionPDF
                        broadcastName={broadcastName}
                        verbalMention={mention}
                        currencyCode={client.currency.code}
                        videoUrl={window.location.href}
                    />
                </div>
            </div>,
            pdfTestDomNode,
        );
    }

    return (
        <VerbalMentionVideoModal
            verbalMention={mention}
            currencyCode={client.currency.code}
            isDownloadingReport={isDownloadingReport}
            getReport={getReport}
            onClose={handleClose}
            onTrackVideoPlayed={() => {
                if (trackObject && pendo) {
                    pendo.track(
                        'Relo Edge::Broadcast Exposure::Verbal Mentions::Exposure Detail::Play Video',
                        trackObject,
                    );
                }
            }}
        />
    );
};

export default connect(
    ['broadcasts', 'filters', 'client'],
    filterActions,
)(VerbalMentionVideoWrapper);
